import { CONSTANT_LABELS } from "@/constants/constants";
import { Button } from "@tracify/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@tracify/ui/dialog";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";

interface UnmappedAccount {
  account_id: string;
  name?: string;
}

interface UnmappedAccountsWarningDialogProps {
  open: boolean;
  onClose: () => void;
  onProceed: () => void;
  unmappedAccounts: UnmappedAccount[];
  channelType: string;
}

export function UnmappedAccountsWarningDialog({
  open,
  onClose,
  onProceed,
  unmappedAccounts,
  channelType,
}: UnmappedAccountsWarningDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <DialogTitle>Unmapped Ad Accounts</DialogTitle>
              <DialogDescription className="mt-1">
                Some {CONSTANT_LABELS[channelType] ?? channelType} ad accounts
                are not mapped to any stores.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-sm">
            The following ad accounts have no store mappings:
          </p>
          <div className="space-y-2 max-h-[55vh] overflow-y-auto">
            {unmappedAccounts.map((account) => (
              <div
                key={account.account_id}
                className="rounded-lg bg-yellow-100 dark:bg-yellow-900/20 p-3 border border-yellow-400 dark:border-yellow-800"
              >
                <div className="font-medium text-sm">
                  {account.name || "Unnamed Account"}
                </div>
                <div className="text-xs text-muted-foreground">
                  ID: {account.account_id}
                </div>
              </div>
            ))}
          </div>
          <p className="text-sm">
            Data from unmapped accounts will not be included in your analytics
            and will not be visible in the dashboard.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Go Back and Map
          </Button>
          <Button onClick={onProceed}>Continue Anyway</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Hook to detect unmapped accounts and show warning
export function useUnmappedAccountsWarning() {
  const showUnmappedAccountsWarning = (
    accounts: Array<{ account_id: string; name?: string }>,
    accountToStoresMapping: Record<string, string[]>,
    channelType: string,
    onProceed: () => void
  ) => {
    const unmappedAccounts = accounts.filter(
      (account) =>
        !accountToStoresMapping[account.account_id] ||
        accountToStoresMapping[account.account_id].length === 0
    );

    return {
      hasUnmappedAccounts: unmappedAccounts.length > 0,
      unmappedAccounts,
      showWarning: (onClose: () => void) => ({
        unmappedAccounts,
        channelType,
        onProceed,
        onClose,
      }),
    };
  };

  return { showUnmappedAccountsWarning };
}
